import {
  declaredSourceNumber,
  isForwardingProven,
} from '../../shared/lib/businessNumber.js';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  COUNTRY_DEFAULTS,
  DEFAULT_AVAILABILITY,
  defaultGreeting,
  usesDefaultGreeting,
  type GreetingMode,
  type AssistantVoice,
  type Availability,
  type BusinessType,
  type Country,
  type Language,
} from '@acs/shared';
import {
  Banner,
  BrandLoader,
  Button,
  Field,
  Icon,
  Input,
  Skeleton,
  useToast,
} from '@acs/ui';
import { useActiveBusiness } from '../../app/ActiveBusinessLayout.js';
import { isSubscribed } from '../../shared/lib/subscription.js';
import { PrepaidActivation } from '../billing/Activation.js';
import { CheckoutConfirming } from '../billing/CheckoutConfirming.js';
import { LegacyPlanPicker } from '../billing/LegacyPlans.js';
import { useCheckoutReturn } from '../billing/useCheckoutReturn.js';
import { CardSetupPending, useCardSetupReturn } from '../billing/useCardSetupReturn.js';
import { useBillingOverview, usePrepareActivation } from '../billing/prepaidHooks.js';
import { useAutoTopup } from '../billing/AutoTopup.js';
import type { BusinessDoc } from '../../shared/api/converters.js';
import { WeeklyScheduleEditor } from '../schedule-grid/index.js';
import { ServicesEditor } from '../settings/ServicesEditor.js';
import { AssistantFields, FactsFields } from '../settings/AssistantFields.js';
import { businessRenamePatch } from '../settings/rename.js';
import { useAvailability, useSaveAvailability, useSaveSettings, useServices } from '../settings/hooks.js';
import { ForwardingPanel, useForwardingWindow } from './ForwardingPanel.js';
import { STEPS, onboardingProgress, stepIndex, type StepId } from './onboardingSteps.js';
import { useActivateBusiness, useSetBusinessRegion } from '../businesses/index.js';
import { ForwardingUnavailable } from './NumberUnavailable.js';
import { BusinessLineField } from './BusinessLineField.js';
import { formatPhoneDisplay } from '../../shared/lib/phoneFormat.js';
import { businessDisplayNumber } from '../../shared/lib/businessNumber.js';
import { BusinessTypeField } from './BusinessTypeField.js';
import { TryCallStage, type CallStatus } from '../testcall/index.js';
import { CountryField } from './CountryField.js';
import { useCompleteOnboarding } from './hooks.js';
import { useI18n } from '../../i18n/index.js';
import type { TranslationKey } from '../../i18n/index.js';

/** Reassurance copy for the aside — why this step is worth the owner's minute.
 *  The words live in the catalogue (doc 20 D8).
 *
 *  Spelled out per step rather than built as `wizard.aside.${id}.title`: the
 *  number step was renamed `forwarding` and a template cast as a key compiled
 *  happily while two steps printed raw key names to every owner. Written out,
 *  a key the catalogue lacks is a type error. */
const ASIDE: Record<StepId, { title: TranslationKey; body: TranslationKey }> = {
  profile: { title: 'wizard.aside.profile.title', body: 'wizard.aside.profile.body' },
  services: { title: 'wizard.aside.services.title', body: 'wizard.aside.services.body' },
  hours: { title: 'wizard.aside.hours.title', body: 'wizard.aside.hours.body' },
  assistant: { title: 'wizard.aside.assistant.title', body: 'wizard.aside.assistant.body' },
  trycall: { title: 'wizard.aside.trycall.title', body: 'wizard.aside.trycall.body' },
  forwarding: { title: 'wizard.aside.forwarding.title', body: 'wizard.aside.forwarding.body' },
  plan: { title: 'wizard.aside.plan.title', body: 'wizard.aside.plan.body' },
  done: { title: 'wizard.aside.done.title', body: 'wizard.aside.done.body' },
};

/**
 * First-run setup (design template — Portal · Onboarding): a step rail, one task
 * per screen, and a reassurance aside. The owner is held here until every step
 * is satisfied — see `needsOnboarding` and the guard in ActiveBusinessLayout.
 */
export function OnboardingWizardPage() {
  const business = useActiveBusiness();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const { data: services, isLoading: servicesLoading } = useServices(business.id);
  const { data: availability, isLoading: hoursLoading } = useAvailability(business.id);
  const loading = servicesLoading || hoursLoading;

  const progress = onboardingProgress({ business, services, availability });

  // The URL owns the step so Back/refresh behave, but it's clamped: you can't
  // deep-link past work you haven't done.
  const requested = STEPS.findIndex((s) => s.id === params.get('step'));
  const active = requested === -1 ? progress.firstUnfinished : Math.min(requested, progress.firstUnfinished);
  const step = STEPS[active]!;

  // Always write the param — including for step 0. Omitting it would read back
  // as "no step requested" and bounce the owner forward to the first unfinished
  // one, making the first step the only unreachable step in the wizard.
  const goto = (index: number) => setParams({ step: STEPS[index]!.id }, { replace: true });
  // Navigate by step ID, never by a hardcoded index: the steps have been
  // reordered once already, and `goto(3)` silently means something different
  // afterwards while still compiling.
  const to = (id: StepId) => () => goto(stepIndex(id));

  // Pin the step the owner is looking at into the URL. With no `?step=` (they
  // arrived at a bare `/setup` from sign-in or the setup gate) the step is
  // "first unfinished", which is LIVE: the moment a forwarded call proved the
  // line, the step under the owner's feet turned into Activate. A step shows
  // its own success and the owner moves on with Continue — never by itself.
  // Other params (`session_id`, `canceled`) are kept for the step to read.
  const unpinned = !loading && requested === -1;
  useEffect(() => {
    if (!unpinned) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('step', step.id);
        return next;
      },
      { replace: true },
    );
  }, [unpinned, step.id, setParams]);

  // Every step opens at its top. The router keeps the window's scroll across
  // a search-param change, so on a phone Continue (at the bottom of a long
  // step) used to land the owner halfway down the next one. Also runs on
  // mount, which covers the hop from business creation into Services.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [step.id]);

  if (loading) {
    return (
      <div className="wizard">
        <Skeleton height={56} radius="var(--tc-radius-md)" />
        <Skeleton height={360} radius="var(--tc-radius-lg)" style={{ marginTop: 20 }} />
      </div>
    );
  }

  return (
    <WizardFrame
      active={active}
      firstUnfinished={progress.firstUnfinished}
      onGoto={goto}
      asideExtra={step.id === 'trycall' ? <TryCallIdeas services={services} /> : undefined}
      // Activate carries its own summary column (design v4): the reassurance
      // aside would squeeze it under the package list on a laptop screen.
      wide={step.id === 'plan'}
    >
      {step.id === 'profile' && <ProfileStep business={business} onDone={to('services')} />}
      {step.id === 'services' && (
        <ServicesStep
          businessId={business.id}
          ready={progress.done[stepIndex('services')]!}
          onBack={to('profile')}
          onDone={to('hours')}
        />
      )}
      {step.id === 'hours' && (
        <HoursStep
          businessId={business.id}
          availability={availability}
          onBack={to('services')}
          onDone={to('assistant')}
        />
      )}
      {step.id === 'assistant' && (
        <AssistantStep business={business} onBack={to('hours')} onDone={to('trycall')} />
      )}
      {step.id === 'trycall' && (
        <TryCallStep business={business} onBack={to('assistant')} onDone={to('forwarding')} />
      )}
      {step.id === 'forwarding' && (
        <ForwardingStep
          business={business}
          ready={progress.done[stepIndex('forwarding')]!}
          onBack={to('trycall')}
          onDone={to('plan')}
        />
      )}
      {step.id === 'plan' && <PlanStep business={business} onBack={to('forwarding')} onDone={to('done')} />}
      {step.id === 'done' && <DoneStep business={business} onExit={(path) => navigate(path)} />}
    </WizardFrame>
  );
}

/**
 * The wizard's furniture: the step rail, the task panel and the reassurance
 * aside. Shared with business creation, which is step 1 of this same flow.
 */
export function WizardFrame({
  active,
  firstUnfinished,
  onGoto,
  asideExtra,
  wide,
  children,
}: {
  active: number;
  /** Steps beyond this are not reachable yet. */
  firstUnfinished: number;
  /** Omit to make the rail purely indicative (nothing to navigate to yet). */
  onGoto?: (index: number) => void;
  /** Step-specific aside content, under the reassurance copy. */
  asideExtra?: React.ReactNode;
  /** Full width, no aside — for a step with its own side column. */
  wide?: boolean;
  children: React.ReactNode;
}) {
  const { t } = useI18n();
  const step = STEPS[active]!;
  return (
    <div className="wizard">
      <ol className="wizard-rail" aria-label={t('wizard.progress')}>
        {STEPS.map((s, i) => {
          // Only the steps *behind* the current one are filled in. A later step
          // may well be finished, but showing it green while you stand earlier
          // in the rail makes the progress bar read backwards.
          const state = i < active ? 'done' : i === active ? 'current' : 'todo';
          return (
            <li key={s.id} className={`wizard-rail__step is-${state}`}>
              <button
                type="button"
                className="wizard-rail__btn"
                // Revisiting finished work is fine; jumping ahead is not.
                disabled={!onGoto || i > firstUnfinished}
                aria-current={i === active ? 'step' : undefined}
                onClick={() => onGoto?.(i)}
              >
                <span className="wizard-rail__mark">
                  {state === 'done' ? <Icon name="check" size={14} strokeWidth={3} /> : i + 1}
                </span>
                <span className="wizard-rail__label">{t(s.labelKey)}</span>
              </button>
              {i < STEPS.length - 1 && <span className="wizard-rail__line" aria-hidden />}
            </li>
          );
        })}
      </ol>

      {wide ? (
        <div className="wizard-body wizard-body--wide">
          <div className="wizard-main">{children}</div>
          <p className="wizard-aside__foot wizard-body__foot">
            <Icon name="shield" size={15} /> {t('wizard.gdpr')}
          </p>
        </div>
      ) : (
        <div className="wizard-body">
          <div className="wizard-main">{children}</div>
          <aside className="wizard-aside">
            <div className="wizard-aside__title">{t(ASIDE[step.id].title)}</div>
            <p className="wizard-aside__body">{t(ASIDE[step.id].body)}</p>
            {asideExtra}
            <p className="wizard-aside__foot">
              <Icon name="shield" size={15} /> {t('wizard.gdpr')}
            </p>
          </aside>
        </div>
      )}
    </div>
  );
}

/** Heading + lede + the step's own content, in one consistent frame. */
export function Step({
  title,
  lede,
  children,
  back,
  backSlot,
  next,
  hideHeading,
}: {
  title: string;
  lede: string;
  /** Keep the title for screen readers only, and drop the lede — for a step
   *  whose own content (a success banner) already says where the owner is. */
  hideHeading?: boolean;
  children: React.ReactNode;
  back?: () => void;
  /** Replaces the Back slot — business creation offers Cancel there instead. */
  backSlot?: React.ReactNode;
  next?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
    hint?: string;
    /** A skip reads as secondary; the default is the primary action. */
    variant?: 'primary' | 'secondary';
  };
}) {
  const { t } = useI18n();
  return (
    <>
      <h1 className={hideHeading ? 'visually-hidden' : 'wizard-title'}>{title}</h1>
      {!hideHeading && <p className="wizard-lede">{lede}</p>}
      {children}
      {(back || next) && (
        <div className="wizard-nav">
          {backSlot ?? (back ? (
            <Button variant="ghost" leadingIcon={<Icon name="arrow-left" size={16} />} onClick={back}>
              {t('wizard.back')}
            </Button>
          ) : (
            <span />
          ))}
          {next && (
            <span className="wizard-nav__next">
              {next.hint && next.disabled && <span className="muted wizard-nav__hint">{next.hint}</span>}
              <Button
                size="lg"
                variant={next.variant}
                disabled={next.disabled}
                loading={next.loading}
                trailingIcon={<Icon name="arrow-right" size={16} />}
                onClick={next.onClick}
              >
                {next.label}
              </Button>
            </span>
          )}
        </div>
      )}
    </>
  );
}

/**
 * Business facts only: what it's called and where it operates.
 *
 * The country is editable here — this is the owner's last easy chance to fix a
 * wrong pick from the creation screen, and it must happen before the number
 * step, because the number is bought in this country and freezes it. Applied
 * through `setBusinessRegion` (which also moves the scheduling timezone) rather
 * than the plain settings write, which no longer accepts either field.
 */
export function ProfileStep({ business, onDone }: { business: BusinessDoc; onDone: () => void }) {
  const { t } = useI18n();
  const save = useSaveSettings(business.id);
  const setRegion = useSetBusinessRegion(business.id);
  const [name, setName] = useState(business.name);
  const [country, setCountry] = useState<Country>(business.country);
  const [businessType, setBusinessType] = useState<BusinessType | null>(business.businessType);

  // Open until setup is finished (doc 23) — the same condition the server's
  // `businessTypeEditable()` applies. The wizard only runs for an unfinished
  // business, so in practice this is always editable here; the guard keeps the
  // control honest if that ever stops being true.
  const typeLocked = business.onboardingCompletedAt != null && business.businessType != null;
  const valid = name.trim().length > 0 && businessType != null;
  const countryChanged = country !== business.country;
  // The number pins the country (doc 13a) — an owner who already activated and
  // walked back here can still rename, but not move country.
  const countryLocked = business.forwardNumber != null;
  const pending = save.isPending || setRegion.isPending;

  const submit = async () => {
    if (countryChanged && !countryLocked) await setRegion.mutateAsync(country);
    if (name.trim() !== business.name) {
      // A greeting that is still our default says the business's name out
      // loud, so a rename carries it along in the same write — otherwise callers
      // would hear the old name. Words the owner wrote themselves are never
      // touched. Same rule as Settings › Business (doc 68).
      await save.mutateAsync(businessRenamePatch(business, name).patch);
    }
    if (!typeLocked && businessType != null && businessType !== business.businessType) {
      await save.mutateAsync({ businessType });
    }
    onDone();
  };

  return (
    <Step
      title={t('wizard.profile.title')}
      lede={t('wizard.profile.lede')}
      next={{
        label: t('wizard.saveAndContinue'),
        loading: pending,
        disabled: !valid,
        hint: t('wizard.profile.nameRequired'),
        onClick: () => void submit(),
      }}
    >
      <BusinessFields
        name={name}
        onName={setName}
        businessType={businessType}
        onBusinessType={setBusinessType}
        businessTypeLocked={typeLocked}
        country={country}
        onCountry={setCountry}
        savedCountry={business.country}
        countryLocked={countryLocked}
      />
      {countryChanged && !countryLocked && (
        <Banner tone="info">
          {t('wizard.profile.regionChange', { timezone: COUNTRY_DEFAULTS[country].timezone })}
        </Banner>
      )}
      {(save.isError || setRegion.isError) && (
        <Banner tone="danger">
          {(setRegion.error as Error | null)?.message ?? t('wizard.saveFailed')}
        </Banner>
      )}
    </Step>
  );
}

/**
 * The assistant, all of it: how it sounds (language, voice, greeting, call
 * length) and what it knows about the business (address, notes) — the same
 * fields Settings › Assistant shows, from the same components, saved in one
 * write.
 *
 * It used to ask for the language and the greeting only, so an owner reached
 * the Try step with a voice they had never chosen and an assistant that could
 * not say where the business is — and met the rest of the questions for the
 * first time in Settings, after going live.
 *
 * Right before Try: everything here is something the owner then hears in the
 * test call, which is the cheapest moment to notice it is wrong.
 */
function AssistantStep({
  business,
  onBack,
  onDone,
}: {
  business: BusinessDoc;
  onBack: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const save = useSaveSettings(business.id);
  // Default the language from the country and the greeting from the name, so
  // this step is a confirmation rather than a blank page.
  const initialLanguage = business.language || COUNTRY_DEFAULTS[business.country].language;
  const [language, setLanguage] = useState<Language>(initialLanguage);
  // Doc 71: the standard greeting (rendered from the current name) unless the
  // owner already wrote their own words.
  const [greetingMode, setGreetingMode] = useState<GreetingMode>(usesDefaultGreeting(business) ? 'default' : 'custom');
  const [greeting, setGreeting] = useState(
    usesDefaultGreeting(business) ? defaultGreeting(business.name, initialLanguage) : business.greeting,
  );
  const [voice, setVoice] = useState<AssistantVoice>(business.voice);
  const [maxCallSeconds, setMaxCallSeconds] = useState(business.maxCallSeconds);
  const [address, setAddress] = useState(business.facts.address);
  const [factsText, setFactsText] = useState(business.facts.text);

  // The standard greeting's preview follows the language by itself; owner-
  // written text stays as written.
  const valid = greetingMode === 'default' || greeting.trim().length > 0;

  return (
    <Step
      title={t('wizard.assistant.title')}
      lede={t('wizard.assistant.lede')}
      back={onBack}
      next={{
        label: t('wizard.saveAndContinue'),
        loading: save.isPending,
        disabled: !valid,
        hint: t('wizard.assistant.greetingRequired'),
        onClick: () =>
          save.mutate(
            {
              language,
              greetingMode,
              greeting: greetingMode === 'default' ? defaultGreeting(business.name.trim(), language) : greeting.trim(),
              voice,
              maxCallSeconds,
              facts: { address: address.trim(), text: factsText.trim() },
            },
            { onSuccess: onDone },
          ),
      }}
    >
      <h2 className="wizard-subhead">{t('wizard.assistant.soundTitle')}</h2>
      <AssistantFields
        language={language}
        onLanguage={setLanguage}
        voice={voice}
        onVoice={setVoice}
        greeting={greeting}
        onGreeting={setGreeting}
        greetingMode={greetingMode}
        onGreetingMode={setGreetingMode}
        businessName={business.name}
        maxCallSeconds={maxCallSeconds}
        onMaxCallSeconds={setMaxCallSeconds}
        greetingRequired
      />

      <h2 className="wizard-subhead">{t('settings.facts.title')}</h2>
      <p className="field-hint wizard-subhead__hint">{t('settings.facts.hint')}</p>
      <FactsFields address={address} onAddress={setAddress} text={factsText} onText={setFactsText} />

      {save.isError && <Banner tone="danger">{t('wizard.saveFailed')}</Banner>}
    </Step>
  );
}

function ServicesStep({
  businessId,
  ready,
  onBack,
  onDone,
}: {
  businessId: string;
  ready: boolean;
  onBack: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  return (
    <Step
      title={t('wizard.services.title')}
      lede={t('wizard.services.lede')}
      back={onBack}
      next={{
        label: t('wizard.continue'),
        disabled: !ready,
        hint: t('wizard.services.hint'),
        onClick: onDone,
      }}
    >
      {/* Saves per row as you add them — nothing to lose if you wander off.
          Bare: the step is already a panel, and a card inside it was a second
          border and a second set of paddings around the same form. */}
      <ServicesEditor businessId={businessId} bare />
    </Step>
  );
}

function HoursStep({
  businessId,
  availability,
  onBack,
  onDone,
}: {
  businessId: string;
  availability: Availability | null | undefined;
  onBack: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const save = useSaveAvailability(businessId);
  const [hours, setHours] = useState<Availability['hours']>(
    () => availability?.hours ?? DEFAULT_AVAILABILITY.hours,
  );

  const hasAny = Object.values(hours).some((intervals) => (intervals?.length ?? 0) > 0);

  return (
    <Step
      title={t('wizard.hours.title')}
      lede={t('wizard.hours.lede')}
      back={onBack}
      next={{
        label: t('wizard.saveAndContinue'),
        loading: save.isPending,
        disabled: !hasAny,
        hint: t('wizard.hours.hint'),
        onClick: () =>
          save.mutate({ ...(availability ?? DEFAULT_AVAILABILITY), hours }, { onSuccess: onDone }),
      }}
    >
      <WeeklyScheduleEditor value={hours} onChange={setHours} />
      {save.isError && <Banner tone="danger">{t('wizard.hours.saveFailed')}</Banner>}
    </Step>
  );
}

/**
 * The optional one: hear the assistant before wiring a phone into it.
 *
 * Never blocks (its progress entry is hard-coded true), because a step that can
 * fail for reasons outside the product — no microphone, a browser that refuses
 * permission, an owner who simply does not want to talk to a computer — must
 * not be the thing standing between them and a working receptionist.
 *
 * Free of the paywall on purpose: the owner's own test calls never consume an
 * allowance (doc 26 D3), which is what makes "try it before you pay" honest
 * rather than a demonstration of something they have not bought.
 *
 * `demo` — and that is the whole difference from Settings › Test call. This one
 * leaves NOTHING behind: no appointment, no client, no transcript, no call
 * record. An owner meeting the product should be able to say anything into it
 * without wondering what they have just put in their calendar. The gateway
 * enforces it on a path that has no writes to reach, not on a flag this screen
 * promises to respect.
 */
function TryCallStep({
  business,
  onBack,
  onDone,
}: {
  business: BusinessDoc;
  onBack: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const [status, setStatus] = useState<CallStatus>('idle');
  const tried = status === 'ended';
  return (
    <Step
      title={t('wizard.trycall.title')}
      lede={t('wizard.trycall.lede')}
      back={onBack}
      // Labelled as a skip, not as "continue", until a call has actually
      // happened: the wizard should not imply the owner has done something
      // they may well have just read past.
      next={
        tried
          ? { label: t('wizard.continue'), onClick: onDone }
          : { label: t('wizard.trycall.skip'), onClick: onDone, variant: 'secondary' }
      }
    >
      <TryCallStage business={business} onAdjustAssistant={onBack} onStatus={setStatus} />
    </Step>
  );
}

/**
 * The Try step's aside: things to say, so an owner facing a microphone is not
 * left wondering what a "customer" would ask. One names a real service.
 */
function TryCallIdeas({ services }: { services: { name: string; active?: boolean }[] | undefined }) {
  const { t } = useI18n();
  const service = services?.find((s) => s.active !== false && s.name.trim())?.name.trim();
  const ideas = [
    t('tryCall.idea.book'),
    service ? t('tryCall.idea.price', { service }) : t('tryCall.idea.priceGeneric'),
    t('tryCall.idea.open'),
    t('tryCall.idea.move'),
  ];
  return (
    <ul className="wizard-aside__ideas">
      {ideas.map((idea) => (
        <li key={idea}>“{idea}”</li>
      ))}
    </ul>
  );
}

/**
 * The owner's own line: which number it is, and pointing it at the assistant.
 *
 * ONE step again. It was briefly two — declare the number early, dial the
 * divert at the very end after paying — which read well on paper and is wrong
 * in practice: switching call forwarding on can mean ringing an operator and
 * waiting days, and a card taken before that lands pays for a line that may
 * never connect. So the whole business of the owner's number lives here, before
 * the plan step, and the money is asked for once the divert is proven.
 *
 * What stops a connected-but-unpaid line from answering real customers is NOT
 * this screen — it is the gateway, which refuses those calls before any model
 * is involved (`serviceBlockReason` → `setup_unpaid`). The line is connected
 * and inert, which is what lets the owner take their time here.
 */
function ForwardingStep({
  business,
  ready,
  onBack,
  onDone,
}: {
  business: BusinessDoc;
  ready: boolean;
  onBack: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const activate = useActivateBusiness();

  const fwd = business.forwardNumber;
  // The declaration outlives the field it is stored in: before provisioning it
  // sits in `pendingSourceNumber`, after it in `forwardNumber.sourceNumber`.
  // Reading only the latter made "have they told us their line?" answerable
  // only AFTER we had given them a number — the exact inversion of this flow.
  // Formatted for the lede below — the owner reads it there and nowhere else in
  // this step, and every other number on screen goes through the same grouping.
  const declared = formatPhoneDisplay(declaredSourceNumber(business));
  // Doc 18: the check is finished only when a forwarded call has proved the
  // owner's line. Stated once, in `isForwardingProven`.
  const verified = isForwardingProven(business);
  // One window between the line field below and the panel's check: the field is
  // frozen while a check runs, and two copies of that state would disagree the
  // moment the owner cancelled one.
  const checkWindow = useForwardingWindow(business);

  // Assign a forwarding target as soon as the owner has told us their line, and
  // do it without asking. Which of our numbers their divert lands on is an
  // implementation detail — there is nothing for them to choose and nothing to
  // celebrate, and the button this replaces ("Get my number") was actively
  // misleading. Fires once: `isIdle` goes false the moment the mutation starts,
  // and a failure surfaces as a retry rather than a loop.
  const needsTarget = declared != null && fwd == null;
  useEffect(() => {
    if (needsTarget && activate.isIdle) activate.mutate(business.id);
  }, [needsTarget, activate, business.id]);

  // ── Step order is the product decision here (doc 18 §7.5) ─────────────────
  // This used to open with a "Get my number" button, which reads as "here is
  // your new business number" — the single most damaging thing we can imply.
  // The owner keeps their own number; ours is a forwarding target they dial
  // once and never advertise. So the flow asks for THEIR number first, and ours
  // only appears afterwards, framed as somewhere to point the divert.
  if (!declared) {
    return (
      <Step title={t('wizard.number.askTitle')} back={onBack} lede={t('wizard.number.askLede')}>
        <BusinessLineField business={business} />
        <p className="muted" style={{ fontSize: 13 }}>{t('wizard.forwarding.payLater')}</p>
      </Step>
    );
  }

  // Declared but no forwarding target yet — assign one. Deliberately silent and
  // automatic: which number their divert lands on is our plumbing, not a
  // decision for the owner to make or a milestone to celebrate.
  if (!fwd) {
    return (
      <Step
        title={t('wizard.number.settingUp')}
        lede={t('wizard.number.settingUpLede')}
        back={onBack}
      >
        {activate.isError ? (
          // Nothing is bought: this is a slot on our shared line, so a failure
          // is "not live in this country" or "ours — try again", and nothing in
          // between (see `ForwardingUnavailable`).
          <ForwardingUnavailable
            businessName={business.name}
            country={business.country}
            error={activate.error}
            onRetry={() => activate.mutate(business.id)}
            retrying={activate.isPending}
          />
        ) : (
          <p className="row" style={{ gap: 10, flexWrap: 'nowrap' }}>
            <BrandLoader size={24} />
            <span className="muted">{t('wizard.number.oneMoment')}</span>
          </p>
        )}
      </Step>
    );
  }

  return (
    <Step
      title={t(verified ? 'wizard.number.connectedTitle' : 'wizard.number.connectTitle')}
      lede={t(verified ? 'wizard.number.connectedLede' : 'wizard.number.connectLede', {
        number: declared ?? '',
      })}
      back={onBack}
      next={{ label: t('wizard.continue'), disabled: !ready, onClick: onDone }}
    >
      {/* The line above the instructions, editable, because everything below is
          about it: which line to dial the code on, which line to ring for the
          check. An owner who spots a typo while reading the dial codes fixes it
          where they are — and it is frozen while a check is running, since the
          arriving call is compared against whatever is stored.

          The panel itself is the identical component Settings › Number renders.
          The wizard used to carry its own copy of these instructions and its own
          verification block, which is how the two ended up describing the same
          task differently. */}
      <BusinessLineField business={business} locked={checkWindow.waiting ? 'check' : null} />
      <ForwardingPanel business={business} checkWindow={checkWindow} />

      {!verified && (
        <p className="muted" style={{ fontSize: 13 }}>{t('wizard.forwarding.payLater')}</p>
      )}
    </Step>
  );
}

/**
 * Activate — the last real step (doc 26 §3a, doc 63).
 *
 * The subscription widget lives IN the step: the owner sees the price, the
 * included minutes, an optional package and auto top-up without leaving the
 * wizard. Checkout itself is Stripe's page, and it returns STRAIGHT here
 * (`?step=plan&session_id=…`) — no intermediate success page. The step reads
 * the subscription from Stripe itself (`useCheckoutReturn`, shared with
 * Settings › Billing) and shows "confirming" until the live document says
 * paid; then the widget shows the minutes arriving.
 *
 * Opening the step puts an eligible business on the included-minutes offer
 * (`prepareActivation`, server-side and idempotent), so the price on screen
 * and what the first paid invoice grants always agree.
 *
 * A legacy-billed business (no prepaid wallet, e.g. a currency the offer does
 * not cover) gets the monthly plan cards in the step itself.
 */
function PlanStep({
  business,
  onBack,
  onDone,
}: {
  business: BusinessDoc;
  onBack: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const subscribed = isSubscribed(business);
  const prepare = usePrepareActivation(business.id);

  // Back from Checkout. Until the live document says paid, the widget would
  // offer the subscription AGAIN to an owner who has just paid for it, so it
  // waits behind a "confirming" note instead.
  const ret = useCheckoutReturn(business);
  // Back from Stripe's card setup (Replace / Add card in auto top-up).
  const cardSetup = useCardSetupReturn(business.id);
  const asked = useRef(false);
  useEffect(() => {
    if (subscribed || ret.confirming || asked.current) return;
    asked.current = true;
    prepare.mutate();
  }, [subscribed, prepare]);
  const prepaid = business.billingSummary.mode === 'prepaid_v1';
  // Auto top-up has no save bar of its own here: the step's Continue saves it
  // ("Save and continue") and then moves on to the Success step.
  const overview = useBillingOverview(business);
  const autoTopup = useAutoTopup(business, overview.data, { active: prepaid && subscribed && !ret.confirming, mode: 'onboarding', onDone });
  // Until the enrollment answers, a legacy-looking business may be about to
  // become prepaid — do not flash the old plan cards at it.
  const settling = !prepaid && !subscribed && prepare.isPending;

  return (
    <Step
      title={t('steps.plan.label')}
      lede={t('steps.plan.blurb')}
      // Subscribed: the top-up view opens on its success banner, so the
      // "Activate / Subscribe so…" heading would contradict it.
      hideHeading={prepaid && subscribed && !ret.confirming}
      back={onBack}
      // On to go-live, not to the finish line: paying is what UNLOCKS pointing
      // the line at the assistant, and it is the last thing left.
      next={!subscribed || ret.confirming ? undefined : autoTopup.ready ? autoTopup.next : { label: t('wizard.continue'), onClick: onDone }}
    >
      <CardSetupPending setup={cardSetup} />
      {ret.confirming ? (
        <CheckoutConfirming ret={ret} />
      ) : prepaid ? (
        <PrepaidActivation business={business} canceled={ret.canceled} autoTopup={autoTopup} />
      ) : settling ? (
        <Skeleton height={320} radius="var(--tc-radius-lg)" />
      ) : subscribed ? (
        <Banner tone="success" title={t('wizard.plan.done.title')}>
          {t('wizard.plan.done.body')}
        </Banner>
      ) : (
        // A legacy business (a currency the offer does not cover): its plans,
        // right here in the step.
        <LegacyPlanPicker business={business} canceled={ret.canceled} />
      )}
    </Step>
  );
}

function DoneStep({ business, onExit }: { business: BusinessDoc; onExit: (to: string) => void }) {
  const { t, tn } = useI18n();
  const complete = useCompleteOnboarding(business.id);
  const { toast } = useToast();
  // Navigating straight away is safe: `useCompleteOnboarding` writes the
  // completion into the businesses cache before this runs, so the gate in
  // `ActiveBusinessLayout` already sees a finished business and lets the chosen
  // destination through. Waiting for the Firestore snapshot instead would never
  // work — it re-renders the layout, which unmounts this component before any
  // effect here could fire.
  const finish = (to: string) =>
    complete.mutate(undefined, {
      onSuccess: () => {
        toast({
          title: t('wizard.done.toast'),
          description: t('wizard.done.toastBody', { business: business.name }),
          tone: 'success',
        });
        onExit(to);
      },
    });

  const busy = complete.isPending;
  // The owner's OWN line — the one callers dial — never our forwarding
  // target, which may be shared with other businesses and must not read as
  // "your new business number" (see businessDisplayNumber).
  const line = businessDisplayNumber(business).number;

  return (
    <div className="wizard-done">
      <span className="wizard-done__icon">
        <Icon name="check" size={30} strokeWidth={3} />
      </span>
      <h1 className="wizard-title">{t('wizard.done.title')}</h1>
      <p className="wizard-lede">
        {line
          ? tn('wizard.done.lede', { number: <strong className="mono">{formatPhoneDisplay(line)}</strong> })
          : t('wizard.done.ledeNoNumber')}
      </p>
      <div className="wizard-done__actions">
        {/* The plan is chosen by now — it is a step of its own (doc 26 §3a),
            and `completeOnboarding` refuses without one — so this button goes
            where it always meant to: the dashboard. */}
        <Button size="lg" loading={busy} onClick={() => finish(`/b/${business.id}`)}>
          {t('wizard.done.dashboard')}
        </Button>
      </div>
      {complete.isError && <Banner tone="danger">{t('wizard.done.failed')}</Banner>}
    </div>
  );
}

/**
 * Business facts — name and country. Shared by the wizard's profile step and by
 * business creation, so the first thing an owner fills in is the same either
 * way. Assistant settings deliberately do NOT live here: see the assistant step.
 */
export function BusinessFields({
  name,
  onName,
  businessType,
  onBusinessType,
  businessTypeLocked,
  country,
  onCountry,
  savedCountry,
  countryLocked,
}: {
  name: string;
  onName: (v: string) => void;
  /** What kind of business — null until answered (doc 23). */
  businessType: BusinessType | null;
  onBusinessType: (v: BusinessType) => void;
  /** Setup is finished: fixed from then on, so the control is read-only. */
  businessTypeLocked?: boolean;
  country: Country;
  onCountry: (v: Country) => void;
  /** The stored country, where there is one — see {@link CountryField}. Absent
   * on the creation screen, which has no business yet. */
  savedCountry?: Country;
  /** Set once a number is held — its country is this business's country. */
  countryLocked?: boolean;
}) {
  const { t } = useI18n();
  return (
    <>
      <Field
        label={t('wizard.fields.businessName')}
        required
        hint={t('wizard.fields.businessNameHint')}
      >
        <Input value={name} onChange={(e) => onName(e.target.value)} maxLength={120} />
      </Field>

      {/* Before the country, which is the only other choice here: what the
          business IS comes before where it operates, and the type is the one
          answer that is fixed once setup is finished. */}
      <BusinessTypeField
        value={businessType}
        onChange={onBusinessType}
        locked={businessTypeLocked}
      />

      <CountryField
        country={country}
        onCountry={onCountry}
        savedCountry={savedCountry}
        locked={countryLocked}
      />
    </>
  );
}

export { stepIndex };
